// @ts-nocheck
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useQuery, useMutation, useAction } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { useAuth } from '@/hooks/useAuth';
import { SlideSelector } from '@/components/ui/slide-selector';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { TimezoneSelect } from '@/components/ui/timezone-select';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import { Doc, Id } from '@/convex/_generated/dataModel';
import {
	Select,
	SelectTrigger,
	SelectValue,
	SelectContent,
	SelectItem,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { AnimatePresence, motion } from 'framer-motion';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Temporal } from '@js-temporal/polyfill';
import { Switch } from '@/components/ui/switch';
import { useConfetti } from '@/hooks/useConfetti';
import { ScheduleCard } from './ScheduleCard';
import { ScheduleForm } from './ScheduleForm';
import { useScheduleStore } from '@/stores/scheduleStore';
import {
	getPodModeCapability,
	getPodPlatformLabel,
} from '@/types/pods';

function getTimeInTimezone(timezone: string) {
	const now = new Date();

	// Get current time in the specified timezone
	const timeInTz = now.toLocaleString('en-US', {
		timeZone: timezone,
		hour12: false,
		hour: '2-digit',
		minute: '2-digit',
	});

	return timeInTz; // Returns format like "14:35"
}

function convertTimeToNewTimezone(
	time: string,
	fromTimezone: string,
	toTimezone: string
) {
	const [hours, minutes] = time.split(':').map(Number);
	return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

function getTimeInFiveMinutes(timezone: string) {
	const now = new Date();
	now.setMinutes(now.getMinutes() + 5);

	// Get time in the specified timezone
	const timeInTz = now.toLocaleString('en-US', {
		timeZone: timezone,
		hour12: false,
		hour: '2-digit',
		minute: '2-digit',
	});

	return timeInTz; // Returns format like "14:35"
}

function getActionDisplay(schedule: any, activeUrlCount?: number) {
	// For scraper task
	if (schedule.type === 'post_scraper') {
		const actionCount = schedule.metadata?.actionCount;
		const postsPerUsername = actionCount ?? 50;

		// Use real-time active URL count instead of outdated metadata
		const urlCount = activeUrlCount ?? schedule.metadata?.urlCount ?? 0;
		const totalPosts = postsPerUsername * urlCount;

		// Debug logging
		console.log('🔍 Source Import Display Debug:', {
			scheduleId: schedule._id,
			podId: schedule.targetId,
			activeUrlCount,
			metadataUrlCount: schedule.metadata?.urlCount,
			finalUrlCount: urlCount,
			postsPerUsername,
			totalPosts,
			source: activeUrlCount !== undefined ? 'real-time' : 'metadata',
		});

		if (urlCount === 0) {
			return 'No active URLs to import';
		} else if (urlCount === 1) {
			return `Importing 1 URL ${totalPosts} latest posts`;
		} else {
			return `Importing ${urlCount} URLs (50 ea.) Total ${totalPosts} latest posts`;
		}
	}

	// For create comments task
	if (schedule.type === 'pod_start') {
		const actionCount = schedule.metadata?.actionCount;
		if (!actionCount || actionCount === 'ALL')
			return 'Creating all available comments';
		if (actionCount === 'ONE') return 'Creating one comment';
		return `Creating ${actionCount} comments`;
	}

	// For publish comments task
	if (schedule.type === 'profile_comment' || schedule.type === 'comments') {
		const commentsPerRun = schedule.metadata?.commentsPerRun;
		if (commentsPerRun === 'ALL')
			return 'Publishing all available comments';
		if (commentsPerRun === 'ONE') return 'Publishing one comment';
		return `Publishing ${commentsPerRun} comments`;
	}

	return null;
}

export default function ScheduleManagement() {
	const updateSchedule = useMutation(api.schedules.update);
	const { user, isSuperAdmin, isAdmin } = useAuth();
	const location = useLocation();
	const navigate = useNavigate();
	const { toast } = useToast();
	const { triggerConfetti, Confetti } = useConfetti();
	const { expandedGroups, setExpandedGroup, initializeFirstGroup } =
		useScheduleStore();

	const podFromState = location.state?.selectedPod || location.state?.podId;

	// State
	const [activeTab, setActiveTab] = React.useState('scraper');
	const [frequency, setFrequency] = React.useState('daily');
	const [selectedDays, setSelectedDays] = React.useState<string[]>([]);
	const [times, setTimes] = React.useState<string[]>([
		getTimeInFiveMinutes(Intl.DateTimeFormat().resolvedOptions().timeZone),
	]);
	const [timezone, setTimezone] = React.useState(
		Intl.DateTimeFormat().resolvedOptions().timeZone
	);
	const [randomRange, setRandomRange] = React.useState({
		minDelay: 5,
		maxDelay: 30,
	});
	const [searchTerm, setSearchTerm] = React.useState('');
	const [selectedPodId, setSelectedPodId] =
		React.useState<Id<'engagementPods'> | null>(
			location.state?.selectedPod?._id || location.state?.podId || null
		);
	const [scheduleSearchTerm, setScheduleSearchTerm] = React.useState('');
	const [isTestMode, setIsTestMode] = React.useState(false);
	const [scheduleToDelete, setScheduleToDelete] = React.useState<{
		id: Id<'schedules'>;
		type: string;
	} | null>(null);
	const [actionCount, setActionCount] = React.useState<{
		value: number | 'ALL' | 'ONE';
		max?: number;
	}>({
		value: activeTab === 'scraper' ? 50 : 'ONE',
	});
	const [errors, setErrors] = React.useState<
		Array<{ message: string; timestamp: string }>
	>([]);

	// Add notification tracking ref at the component level
	const hasShownNotification = React.useRef(false);

	// Reset action count when changing tabs
	React.useEffect(() => {
		setActionCount({
			value: activeTab === 'scraper' ? 50 : 'ONE',
		});
	}, [activeTab]);

	// Track if we've already checked for schedule creation to prevent re-creates
	const hasAttemptedScheduleCreation = React.useRef(false);

	// Queries and Mutations
	const allPods = useQuery(
		api.engagementPods.list,
		user?.id
			? {
					userId: null,
					requestingUserId: user.id as unknown as string,
				}
			: 'skip'
	);

	const selectedPod = allPods?.find(
		(pod: Doc<'engagementPods'>) => pod._id === selectedPodId
	);
	const selectedPodCapability = selectedPod
		? getPodModeCapability(
				selectedPod.platform,
				selectedPod.mode || 'swarm'
			)
		: null;

	const podUrls = useQuery(api.urls.list, {
		podId: selectedPodId || null,
		profileKey:
			selectedPod?.mode === 'broadcast'
				? selectedPod.members[0]?.uploadPostProfileKey
				: null,
	});

	const schedules = useQuery(api.schedules.getByTargetId, {
		targetId: selectedPodId || null,
	});

	// Get all unique pod IDs that have schedules
	const allSchedules = useQuery(api.schedules.getAllSchedules);
	const podIdsWithSchedules = React.useMemo(() => {
		if (!allSchedules) return [];
		return [
			...new Set(allSchedules.map((schedule: any) => schedule.targetId)),
		];
	}, [allSchedules]);

	// Get URL counts for all pods with schedules
	const allPodUrls = useQuery(
		api.urls.list,
		podIdsWithSchedules.length > 0
			? {
					podId: null, // Get all URLs
					userId: user?.id as unknown as Id<'users'>,
					isSuperAdmin: Boolean(isSuperAdmin),
				}
			: 'skip'
	);

	// Create a map of podId -> active URL count
	const podUrlCounts = React.useMemo(() => {
		if (!allPodUrls) return {};

		const counts: Record<string, number> = {};
		allPodUrls.forEach((url: any) => {
			if (url.podId && url.isActive && !url.isArchived) {
				const podId = url.podId;
				counts[podId] = (counts[podId] || 0) + 1;
			}
		});

		console.log('🔍 Pod URL Counts Debug:', {
			totalUrls: allPodUrls.length,
			activeUrls: allPodUrls.filter(
				(url: any) => url.isActive && !url.isArchived
			).length,
			counts,
			urlBreakdown: allPodUrls.map((url: any) => ({
				podId: url.podId,
				isActive: url.isActive,
				isArchived: url.isArchived,
				url: url.url.substring(0, 50),
			})),
		});
		return counts;
	}, [allPodUrls]);

	const createSchedule = useMutation(api.schedules.create);
	const toggleSchedule = useMutation(api.schedules.toggleSchedule);
	const deleteSchedule = useMutation(api.schedules.deleteSchedule);
	const runScheduleAction = useAction<
		typeof api.scheduledTasks.runScheduleNow
	>(api.scheduledTasks.runScheduleNow);

	// Use useEffect to set initial state once schedules are loaded
	React.useEffect(() => {
		if (schedules && schedules.length > 0) {
			initializeFirstGroup(schedules[0].targetId as Id<'engagementPods'>);
		}
	}, [initializeFirstGroup, schedules]);

	// Handlers
	const handleAddTime = () => {
		setTimes([...times, getTimeInTimezone(timezone)]);
	};

	const handleRemoveTime = (index: number) => {
		setTimes(times.filter((_, i) => i !== index));
	};

	const handleSubmit = async () => {
		try {
			if (!selectedPod) {
				handleError('No pod selected');
				return;
			}
			if (selectedPodCapability && !selectedPodCapability.enabled) {
				handleError(
					selectedPodCapability.reason ||
						`${getPodPlatformLabel(selectedPod.platform)} ${selectedPod.mode || 'swarm'} schedules are disabled for this spec`
				);
				return;
			}

			// Add URL check before schedule creation
			if (!podUrls || podUrls.length === 0) {
				handleError(
					'Please add at least one URL to this pod before creating a schedule'
				);
				return;
			}

			// Add check for social profile when scheduling scraper
			if (
				activeTab === 'scraper' &&
				!selectedPod.members?.[0]?.socialUsername
			) {
				toast({
					title: 'Missing Social Profile',
					description:
						'Please attach a social profile to this pod in Engagement Pods before scheduling source import.',
					variant: 'destructive',
				});
				return;
			}

			const typeMap = {
				pod: 'pod_start',
				scraper: 'post_scraper',
				comments: 'profile_comment',
			};
			const scheduleType = typeMap[activeTab as keyof typeof typeMap];

			const existingSchedule = schedules?.find(
				(s: Doc<'schedules'>) => s.type === scheduleType
			);
			if (existingSchedule) {
				toast({
					title: 'Schedule Already Exists',
					description: `A ${formatScheduleType(scheduleType)} already exists for this pod. Please delete it first.`,
					variant: 'destructive',
				});
				return;
			}

			// Convert times to UTC before saving
			const utcTimes = times.map((time) => {
				const [hours, minutes] = time.split(':').map(Number);
				// Create a date at the specified time in the selected timezone
				const date = new Date(Date.UTC(2024, 0, 1, hours, minutes));

				// Adjust for timezone offset
				const tzOffset =
					new Date(
						date.toLocaleString('en-US', { timeZone: timezone })
					).getTime() -
					new Date(
						date.toLocaleString('en-US', { timeZone: 'UTC' })
					).getTime();

				// Apply timezone offset
				const adjustedDate = new Date(date.getTime() - tzOffset);

				const utcHours = adjustedDate.getUTCHours();
				const utcMinutes = adjustedDate.getUTCMinutes();

				return `${utcHours.toString().padStart(2, '0')}:${utcMinutes.toString().padStart(2, '0')}`;
			});

			const schedule = {
				frequency,
				days: frequency === 'weekly' ? selectedDays : undefined,
				times: utcTimes,
				timezone,
				randomRange: frequency === 'random' ? randomRange : undefined,
			};

			console.log('Creating schedule with action count:', {
				actionCount,
				metadata: {
					actionCount: actionCount.value,
					urlCount: podUrls?.length || 0,
					isTestMode,
				},
			});

			const metadata = {
				urlCount: podUrls?.length || 0,
				actionCount: actionCount.value,
				commentsPerRun:
					actionCount.value === 'ONE' ? 1 : actionCount.value,
				isTestMode,
			};

			const scheduleId = await createSchedule({
				type:
					activeTab === 'scraper'
						? 'post_scraper'
						: activeTab === 'pod'
							? 'pod_start'
							: 'comments',
				name: 'Engagement Source Import',
				description:
					'Automatically collect target posts for this engagement pod',
				targetId: selectedPod._id,
				schedule: {
					frequency,
					days: frequency === 'weekly' ? selectedDays : undefined,
					times,
					timezone,
					randomRange,
				},
				metadata,
			});

			triggerConfetti();
			toast({
				title: '✨ Schedule Created',
				description: 'Your schedule has been created successfully.',
			});

			setFrequency('daily');
			setSelectedDays([]);
			setTimes([getTimeInFiveMinutes(timezone)]);
			setRandomRange({ minDelay: 5, maxDelay: 30 });
		} catch (error) {
			handleError(error);
		}
	};

	const handleToggleSchedule = React.useCallback(
		async (schedule: any) => {
			try {
				console.log(
					'UI: Toggling schedule:',
					schedule._id,
					schedule.status
				);
				await toggleSchedule({
					scheduleId: schedule._id,
				});
				console.log('UI: Schedule toggled successfully');

				toast({
					title:
						schedule.status === 'active'
							? '⏸️ Schedule Paused'
							: '▶️ Schedule Resumed',
					description:
						schedule.status === 'active'
							? 'Schedule has been paused and will not run until resumed'
							: 'Schedule is now active and will run at the next scheduled time',
					duration: 4000,
					className:
						schedule.status === 'active'
							? 'bg-orange-500/10 border-orange-500/20'
							: 'bg-green-500/10 border-green-500/20',
				});
			} catch (error) {
				console.error('UI: Failed to toggle schedule:', error);
				toast({
					title: 'Error',
					description:
						error instanceof Error
							? error.message
							: 'Failed to update schedule',
					variant: 'destructive',
				});
			}
		},
		[toggleSchedule, toast]
	);

	const handleDeleteSchedule = (id: Id<'schedules'>, type: string) => {
		setScheduleToDelete({ id, type });
	};

	function formatScheduleType(type: string) {
		switch (type) {
			case 'post_scraper':
				return '⚡️ SOURCE IMPORT';
			case 'pod_start':
				return '✨ CREATE COMMENTS';
			case 'profile_comment':
			case 'comments':
				return '📣 PUBLISH COMMENTS';
			default:
				return type;
		}
	}

	const renderSchedulingControls = (type: string) => {
		return (
			<div className='space-y-4'>
				<Select value={frequency} onValueChange={setFrequency}>
					<SelectTrigger>
						<SelectValue placeholder='Select frequency' />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value='daily'>Daily</SelectItem>
						<SelectItem value='weekly'>Weekly</SelectItem>
						<SelectItem value='random'>Random</SelectItem>
					</SelectContent>
				</Select>

				{frequency === 'weekly' && (
					<div className='space-y-2'>
						<p className='text-sm font-medium'>Select Days</p>
						<div className='flex gap-2 flex-wrap'>
							{[
								'Monday',
								'Tuesday',
								'Wednesday',
								'Thursday',
								'Friday',
								'Saturday',
								'Sunday',
							].map((day) => (
								<div
									key={day}
									className='flex items-center space-x-2'
								>
									<Checkbox
										checked={selectedDays.includes(
											day.toLowerCase()
										)}
										onCheckedChange={(checked) => {
											if (checked) {
												setSelectedDays([
													...selectedDays,
													day.toLowerCase(),
												]);
											} else {
												setSelectedDays(
													selectedDays.filter(
														(d) =>
															d !==
															day.toLowerCase()
													)
												);
											}
										}}
									/>
									<label className='text-sm'>{day}</label>
								</div>
							))}
						</div>
					</div>
				)}

				{frequency === 'random' && (
					<div className='space-y-2'>
						<p className='text-sm font-medium'>
							Random Delay Range (minutes)
						</p>
						<div className='flex gap-2'>
							<Input
								type='number'
								value={randomRange.minDelay}
								onChange={(e) =>
									setRandomRange({
										...randomRange,
										minDelay: parseInt(e.target.value),
									})
								}
								placeholder='Min delay'
							/>
							<Input
								type='number'
								value={randomRange.maxDelay}
								onChange={(e) =>
									setRandomRange({
										...randomRange,
										maxDelay: parseInt(e.target.value),
									})
								}
								placeholder='Max delay'
							/>
						</div>
					</div>
				)}

				{frequency !== 'random' && (
					<div className='space-y-2'>
						<div className='flex items-center justify-between'>
							<p className='text-sm font-medium'>Times</p>
							<Button
								variant='outline'
								size='sm'
								onClick={handleAddTime}
							>
								<span className='flex items-center gap-1'>
									⏰ <span>Add Time</span>
								</span>
							</Button>
						</div>
						{times.map((time, index) => (
							<div
								key={index}
								className='flex items-center gap-2'
							>
								<Input
									type='time'
									value={time}
									onChange={(e) => {
										const newTimes = [...times];
										newTimes[index] = e.target.value;
										setTimes(newTimes);
									}}
									defaultValue={getTimeInTimezone(timezone)}
									className='focus-visible:ring-purple-500 focus-visible:border-purple-500 border-purple-500/20'
								/>
								{index > 0 && (
									<Button
										variant='ghost'
										size='sm'
										onClick={() => {
											setTimes(
												times.filter(
													(_, i) => i !== index
												)
											);
										}}
										className='hover:bg-purple-500/20 hover:text-purple-500'
									>
										✕
									</Button>
								)}
							</div>
						))}
					</div>
				)}

				<TimezoneSelect
					value={timezone}
					onValueChange={handleTimezoneChange}
				/>
			</div>
		);
	};

	const filteredSchedules = React.useMemo(() => {
		if (!schedules) return [];
		return schedules.filter((schedule: Doc<'schedules'>) =>
			schedule.type
				.toLowerCase()
				.includes(scheduleSearchTerm.toLowerCase())
		);
	}, [schedules, scheduleSearchTerm]);

	const groupedSchedules = React.useMemo(() => {
		// Start with filtered schedules
		let schedulesToGroup = filteredSchedules || [];

		// If not a super admin, filter to only show pods the user has access to
		if (!isSuperAdmin && allPods) {
			const userPodIds = allPods.map(
				(pod: Doc<'engagementPods'>) => pod._id
			);
			schedulesToGroup = schedulesToGroup.filter(
				(schedule: Doc<'schedules'>) =>
					userPodIds.includes(
						schedule.targetId as Id<'engagementPods'>
					)
			);
		}

		return schedulesToGroup.reduce(
			(
				acc: Record<string, Doc<'schedules'>[]>,
				schedule: Doc<'schedules'>
			) => {
				const podId = schedule.targetId as string; // Assuming targetId can be used as a string key
				if (!acc[podId]) acc[podId] = [];
				acc[podId].push(schedule);
				return acc;
			},
			{} as Record<string, Doc<'schedules'>[]>
		);
	}, [filteredSchedules, isSuperAdmin, allPods]);

	React.useEffect(() => {
		if (selectedPodId) {
			setExpandedGroup(selectedPodId, true);
		}
	}, [
		groupedSchedules,
		location.state?.fromPodView,
		selectedPodId,
		setExpandedGroup,
	]);

	// Add this useEffect for debugging
	React.useEffect(() => {
		if (selectedPod && schedules) {
			console.log('Selected Pod ID:', selectedPod._id);
			console.log('All Schedules:', schedules);
			console.log(
				'Filtered Schedules:',
				schedules.filter(
					(s: Doc<'schedules'>) => s.targetId === selectedPod._id
				)
			);
			console.log(
				'Running Schedules:',
				schedules.filter(
					(s: Doc<'schedules'>) =>
						s.targetId === selectedPod._id && s.status === 'running'
				)
			);
			console.log(
				'Active Schedules:',
				schedules.filter(
					(s: Doc<'schedules'>) =>
						s.targetId === selectedPod._id && s.status === 'active'
				)
			);
		}
	}, [selectedPod, schedules]);

	// Add useEffect to automatically create a scraper schedule for broadcast mode pods
	// This automatically creates a daily 5 AM scraper task when a user navigates here
	// from the PodExpandedView component and the pod is in broadcast mode
	React.useEffect(() => {
				const autoCreateScraperSchedule = async () => {
			// Skip if we already created a schedule in PodExpandedView (autoScheduled flag)
			if (location.state?.autoScheduled) {
				return;
			}

			// We only want to create a schedule on initial navigation from PodExpandedView
			if (hasAttemptedScheduleCreation.current) {
				return;
			}

			hasAttemptedScheduleCreation.current = true;

			// Check if we came from PodExpandedView and it's a broadcast mode pod
			if (
				location.state?.fromPodView &&
				location.state?.createSchedule === true &&
				selectedPod &&
				(selectedPod.mode === 'broadcast' ||
					location.state?.podMode === 'broadcast') &&
				selectedPodCapability?.enabled !== false &&
				podUrls &&
				podUrls.length > 0
			) {
				// Check if this pod already has a post_scraper schedule
				const existingScraperSchedule = schedules?.find(
					(s: Doc<'schedules'>) =>
						s.type === 'post_scraper' &&
						s.targetId === selectedPod._id
				);

				if (!existingScraperSchedule) {
					console.log(
						'Auto-creating source import schedule for broadcast pod'
					);

					// Set up scraper schedule for 5 AM
					const userTimezone =
						Intl.DateTimeFormat().resolvedOptions().timeZone;

					try {
						// Create 5 AM schedule in user's local timezone
						await createSchedule({
							type: 'post_scraper',
							name: 'Engagement Source Import',
							description:
								'Automatically collect target posts for this engagement pod',
							targetId: selectedPod._id,
							schedule: {
								frequency: 'daily',
								times: ['05:00'],
								timezone: userTimezone,
								randomRange: { minDelay: 5, maxDelay: 30 },
							},
							metadata: {
								urlCount: podUrls.length || 0,
								actionCount: 50,
								isTestMode: false,
							},
						});

						toast({
							title: '✨ Schedule Created',
							description: 'Your pod is now scheduled.',
						});

						triggerConfetti();
					} catch (error) {
						console.error('Error auto-creating schedule:', error);
						toast({
							title: 'Error',
							description:
								error instanceof Error
									? error.message
									: 'Failed to create schedule',
							variant: 'destructive',
						});
					}
				}
			}
		};

		autoCreateScraperSchedule();
	}, [
		location.state?.fromPodView,
		location.state?.podMode,
		location.state?.createSchedule,
		selectedPod,
		selectedPodCapability?.enabled,
		schedules,
		podUrls,
		createSchedule,
		toast,
		triggerConfetti,
		location.state?.autoScheduled,
	]);

	// Add notification for auto-scheduled pods
	React.useEffect(() => {
		// Check if we were redirected from PodExpandedView with autoScheduled flag
		if (
			location.state?.autoScheduled &&
			selectedPod &&
			!hasShownNotification.current
		) {
			// Mark that we've shown the notification
			hasShownNotification.current = true;

			// Display a toast notification
			toast({
				title: '✨ Schedules Created',
				description:
					'Your pod has been set up with both a daily source import (5:00 AM) and a comment publisher (4 times daily).',
			});

			// Trigger confetti effect for visual feedback
			triggerConfetti();
		}
	}, [location.state?.autoScheduled, selectedPod, toast, triggerConfetti]);

	// Add debug log for schedules
	React.useEffect(() => {
		console.log('Schedules Query Result:', {
			selectedPodId,
			schedules,
		});
	}, [selectedPodId, schedules]);

	// Add useEffect to update times when timezone changes
	React.useEffect(() => {
		if (times.length === 1 && times[0] === '09:00') {
			setTimes([getTimeInTimezone(timezone)]);
		}
	}, [times, timezone]);

	// Update the timezone select handler
	const handleTimezoneChange = React.useCallback(
		(newTimezone: string) => {
			const newTimes = times.map((time) =>
				convertTimeToNewTimezone(time, timezone, newTimezone)
			);
			setTimes(newTimes);
			setTimezone(newTimezone);
		},
		[times, timezone]
	);

	const handleError = (error: unknown) => {
		const message = error instanceof Error ? error.message : String(error);

		toast({
			title: 'Error',
			description: message,
			variant: 'destructive',
		});

		setErrors((prev) => [
			...prev,
			{
				message,
				timestamp: new Date().toISOString(),
			},
		]);
	};

	const handleCommentsPerRunChange = async (
		value: string | number,
		schedule: any
	) => {
		await updateSchedule({
			scheduleId: schedule._id,
			metadata: {
				...schedule.metadata,
				commentsPerRun: value === 'ALL' ? value : Number(value),
			},
		});
	};

	const handleRunNow = React.useCallback(
		async (scheduleId: Id<'schedules'>) => {
			console.log('Running schedule:', scheduleId);
			toast({
				title: '🚀 Manual Run Started',
				description: 'The schedule has been triggered manually',
			});

			try {
				await runScheduleAction({ scheduleId });
				console.log('Schedule run completed');
			} catch (error) {
				console.error('Schedule run error:', error);
				toast({
					title: '❌ Error',
					description:
						error instanceof Error
							? error.message
							: 'Unknown error',
					variant: 'destructive',
				});
			}
		},
		[runScheduleAction, toast]
	);

	const renderCount = React.useRef(0);
	React.useEffect(() => {
		renderCount.current += 1;
		console.log('ScheduleManagement render count:', renderCount.current);
	}, []);

	const podOptions = React.useMemo(() => {
		return (allPods || []).map((pod: Doc<'engagementPods'>) => ({
			value: pod._id,
			label: pod.name,
			data: pod,
		}));
	}, [allPods]);

	const handlePodSelect = React.useCallback((value: string) => {
		setSelectedPodId(value as Id<'engagementPods'>);
	}, []);

	return (
		<div className='container mx-auto p-6 pb-80'>
			<div className='flex justify-between items-center mb-6'>
				<h1 className='text-3xl font-bold'>Schedule Management</h1>
			</div>

			{(isSuperAdmin ||
				!location.pathname.includes('/admin/schedules')) && (
				<>
					{!podFromState && (
						<Card className='border-2 border-purple-500/20 mb-6'>
							<CardHeader>
								<CardTitle className='text-xl font-bold text-pink-500'>
									Select Pod
								</CardTitle>
							</CardHeader>
							<CardContent>
								<SearchableSelect<string>
									options={podOptions}
									value={selectedPodId || ''}
									onValueChange={handlePodSelect}
									placeholder='Select a pod...'
								/>
							</CardContent>
						</Card>
					)}

					{selectedPod && (
						<Card className='border-2 border-purple-500/20'>
							<CardHeader>
								<div className='flex items-center justify-between'>
									<CardTitle className='text-2xl font-bold text-pink-500'>
										Pod Scheduler
										<div className='flex items-center gap-2 mt-2 text-base font-normal text-muted-foreground'>
											POD:{' '}
											<span className='text-white text-lg font-semibold'>
												{selectedPod.name}
											</span>
										</div>
										{selectedPod.mode === 'broadcast' && (
											<div className='flex items-center gap-2 mt-2 text-base font-normal text-muted-foreground'>
												Profile:
												<span className='font-semibold text-orange-500'>
													{selectedPod.members[0]
														?.socialUsername ||
														'No Profile Assigned'}
												</span>
											</div>
										)}
										<div className='flex items-center gap-2 mt-2 text-base font-normal text-muted-foreground'>
											URLs Assigned:
											<span className='text-2xl font-bold text-emerald-500'>
												{podUrls?.filter(
													(url: Doc<'urls'>) =>
														!url.isArchived
												).length || 0}
											</span>
										</div>
									</CardTitle>
									<span
										className={cn(
											'px-8 py-4 mx-8 mt-8 rounded-full text-2xl font-bold text-white shadow-lg',
											selectedPod.mode === 'broadcast'
												? 'bg-gradient-to-b from-orange-300 via-orange-500 to-orange-700'
												: 'bg-gradient-to-b from-purple-300 via-purple-500 to-purple-800',
											'transition-all duration-300'
										)}
										style={{
											animation:
												'glow 2s ease-in-out infinite',
											boxShadow: !schedules?.length
												? '0 0 50px rgba(239, 68, 68, 0.8)'
												: schedules.some(
															(
																s: Doc<'schedules'>
															) =>
																s.status ===
																'running'
													  )
													? '0 0 50px rgba(34, 197, 94, 0.8)'
													: schedules.some(
																(
																	s: Doc<'schedules'>
																) =>
																	s.status ===
																	'active'
														  )
														? '0 0 50px rgba(147, 51, 234, 0.8)'
														: '0 0 50px rgba(234, 179, 8, 0.8)',
										}}
									>
										{(
											selectedPod.mode || 'swarm'
										).toUpperCase()}{' '}
										MODE
									</span>
								</div>
							</CardHeader>
							<CardContent>
								<ScheduleForm
									activeTab={activeTab}
									onTabChange={setActiveTab}
									frequency={frequency}
									onFrequencyChange={setFrequency}
									selectedDays={selectedDays}
									onDaysChange={setSelectedDays}
									times={times}
									onTimesChange={setTimes}
									timezone={timezone}
									onTimezoneChange={handleTimezoneChange}
									randomRange={randomRange}
									onRandomRangeChange={setRandomRange}
									isTestMode={isTestMode}
									onTestModeChange={setIsTestMode}
									onSubmit={handleSubmit}
									isAdmin={user?.role === 'admin'}
									actionCount={actionCount}
									onActionCountChange={(value) =>
										setActionCount({ value })
									}
									selectedPod={selectedPod}
									selectedPodId={selectedPod._id}
									schedules={schedules}
									isCapabilityDisabled={
										selectedPodCapability
											? !selectedPodCapability.enabled
											: false
									}
									capabilityDisabledReason={
										selectedPodCapability?.reason
									}
								>
									<span className='flex items-center gap-2 whitespace-nowrap'>
										{activeTab === 'pod' ? (
											<>✨ Schedule Comments Generation</>
										) : activeTab === 'scraper' ? (
											<>⚡️ Schedule Source Import</>
										) : (
											<>📣 Schedule Comments Publishing</>
										)}
									</span>
								</ScheduleForm>
							</CardContent>
						</Card>
					)}
				</>
			)}

			<div className='mt-8'>
				<div className='flex justify-between items-center mb-4'>
					<h3 className='text-xl font-bold text-pink-500'>
						Schedules
						{selectedPod && ` - ${selectedPod.name}`}
					</h3>
					<div className='w-72'>
						<Input
							placeholder='Search schedules...'
							value={scheduleSearchTerm}
							onChange={(e) =>
								setScheduleSearchTerm(e.target.value)
							}
							className='bg-background/50 border-purple-500/20'
						/>
					</div>
				</div>
				<Card className='border-2 border-purple-500/20'>
					<CardContent className='pt-6'>
						{!Object.keys(groupedSchedules).length ? (
							<div className='flex flex-col items-center justify-center py-8 text-center'>
								<div className='text-4xl mb-4'>📅</div>
								<h3 className='text-lg font-semibold mb-2'>
									No Schedules Found
								</h3>
								<p className='text-muted-foreground max-w-sm whitespace-nowrap'>
									{scheduleSearchTerm ? (
										'No schedules match your search. Try adjusting your search terms.'
									) : selectedPod ? (
										<>
											Add schedules to
											<br />
											<span className='font-semibold text-white'>
												{selectedPod.name}
											</span>
											<br />
											using the scheduler above.
										</>
									) : (
										'Select a pod and add schedules to automate Prometheus.'
									)}
								</p>
							</div>
						) : (
							Object.entries(groupedSchedules).map(
								([podId, schedulesForPod]) => {
									const pod = allPods?.find(
										(p: Doc<'engagementPods'>) =>
											p._id === podId
									);

									// Skip rendering if pod is unknown and user is not super admin
									if (!pod && !isSuperAdmin) return null;

									return (
										<div key={podId}>
											<div className='flex items-center gap-2 mb-4'>
												<motion.button
													onClick={() =>
														setExpandedGroup(
															podId as Id<'engagementPods'>,
															!expandedGroups[
																podId
															]
														)
													}
													className='w-8 h-8 rounded-full bg-purple-900 flex items-center justify-center text-white hover:bg-purple-700 transition-colors'
													animate={{
														rotate: expandedGroups[
															podId
														]
															? 180
															: 0,
													}}
													transition={{
														duration: 0.2,
														ease: 'easeInOut',
													}}
												>
													{expandedGroups[podId]
														? '🔽'
														: '▶️'}
												</motion.button>
												<div className='flex items-center gap-2'>
													<span className='text-lg font-semibold'>
														{pod?.name ||
															(isSuperAdmin
																? 'Unknown Pod'
																: '')}
													</span>
													<span className='text-sm text-muted-foreground'>
														(
														{
															(
																schedulesForPod as Doc<'schedules'>[]
															).length
														}{' '}
														schedule
														{(
															schedulesForPod as Doc<'schedules'>[]
														).length !== 1
															? 's'
															: ''}
														)
													</span>
												</div>
											</div>

											<AnimatePresence>
												{expandedGroups[podId] && (
													<motion.div
														initial={{
															opacity: 0,
															height: 0,
														}}
														animate={{
															opacity: 1,
															height: 'auto',
														}}
														exit={{
															opacity: 0,
															height: 0,
														}}
														transition={{
															duration: 0.2,
														}}
														className='overflow-hidden mb-16'
													>
														<div className='space-y-2'>
															{(
																schedulesForPod as Doc<'schedules'>[]
															).map(
																(schedule) => (
																	<ScheduleCard
																		key={
																			schedule._id
																		}
																		schedule={
																			schedule
																		}
																		onDelete={
																			handleDeleteSchedule
																		}
																		onRunNow={
																			handleRunNow
																		}
																		formatScheduleType={
																			formatScheduleType
																		}
																		getActionDisplay={(
																			sched
																		) =>
																			getActionDisplay(
																				sched,
																				podUrlCounts[
																					sched
																						.targetId
																				]
																			)
																		}
																		allPods={
																			allPods ||
																			[]
																		}
																		isSuperAdmin={
																			isSuperAdmin
																		}
																		isAdmin={
																			isAdmin
																		}
																		user={
																			user
																		}
																	/>
																)
															)}
														</div>
													</motion.div>
												)}
											</AnimatePresence>
										</div>
									);
								}
							)
						)}
					</CardContent>
				</Card>
			</div>

			<ConfirmDialog
				isOpen={scheduleToDelete !== null}
				onClose={() => setScheduleToDelete(null)}
				title='Delete Schedule'
				description={`Are you sure you want to delete this ${scheduleToDelete?.type} schedule?`}
				onConfirm={async () => {
					if (scheduleToDelete) {
						await deleteSchedule({
							scheduleId: scheduleToDelete.id,
						});
						setScheduleToDelete(null);
						toast({
							title: '✨ Schedule Deleted',
							description:
								'Schedule has been deleted successfully',
							duration: 3000,
						});
					}
				}}
			/>
			<Confetti />

			{errors.length > 0 && (
				<Card className='mt-4 border-red-500'>
					<CardHeader>
						<CardTitle className='text-red-500'>
							Recent Errors
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className='space-y-2'>
							{errors.slice(-5).map((error, index) => (
								<div
									key={error.timestamp}
									className='flex items-center justify-between p-2 bg-red-50 dark:bg-red-900/20 rounded-lg'
								>
									<div className='flex items-center gap-2'>
										<span className='text-red-500'>⚠️</span>
										<span className='text-sm text-red-700 dark:text-red-300'>
											{error.message}
										</span>
									</div>
									<Button
										variant='ghost'
										size='sm'
										onClick={() =>
											setErrors((prev) =>
												prev.filter(
													(_, i) => i !== index
												)
											)
										}
										className='text-red-500 hover:text-red-700'
									>
										✕
									</Button>
								</div>
							))}
							{errors.length > 0 && (
								<Button
									variant='outline'
									size='sm'
									onClick={() => setErrors([])}
									className='w-full mt-2 text-red-500 hover:text-red-700 border-red-200'
								>
									Clear All Errors
								</Button>
							)}
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
}
